import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AdminRoute = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    const checkAdminAccess = async () => {
      try {
        setIsLoading(true);
        
        // Get current session
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        
        // Immediate redirect if no session
        if (sessionError || !session?.user) {
          if (mounted) {
            setIsAuthorized(false);
            setIsLoading(false);
          }
          return;
        }

        // Fetch user role from database
        const { data: userData, error: userError } = await supabase
          ?.from('users')
          ?.select('role')
          ?.eq('id', session?.user?.id)
          ?.single();

        if (userError || !userData) {
          console.error('Error fetching user role:', userError);
          if (mounted) {
            setIsAuthorized(false);
            setIsLoading(false);
          }
          return;
        }

        // Strict role check - only admin or superadmin allowed
        const hasAccess = userData?.role === 'admin' || userData?.role === 'superadmin';
        
        if (mounted) {
          setIsAuthorized(hasAccess);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Admin access check error:', error);
        if (mounted) {
          setIsAuthorized(false);
          setIsLoading(false);
        }
      }
    };

    // Initial check
    checkAdminAccess();

    // Set up auth state listener for real-time session changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED' || !session) {
        // Immediate redirect on sign out or session loss
        if (mounted) {
          setIsAuthorized(false);
          setIsLoading(false);
        }
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Re-check authorization on sign in or token refresh
        checkAdminAccess();
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [location?.pathname]); // Re-check on every route change within admin

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Verifica autorizzazione...</p>
        </div>
      </div>
    );
  }

  // Immediate redirect for unauthorized access
  if (isAuthorized === false) {
    return <Navigate to="/" replace />;
  }

  // Only render children if explicitly authorized
  return isAuthorized === true ? children : null;
};

export default AdminRoute;